import React, { useEffect } from 'react';
import { Drawer, Form, Select, InputNumber, Button, Space, message, Alert } from 'antd';
import { useRequest } from 'ahooks';
import { getDevice } from '@/api';
import { getFirmwares, createOtaJob } from '@/api/ota';

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  presetDeviceIds?: string[];
}

const CreateJobDrawer: React.FC<Props> = ({ open, onClose, onSuccess, presetDeviceIds }) => {
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();

  const { data: firmwareData } = useRequest(getFirmwares, {
    defaultParams: [{ page: 1, pageSize: 1000 }],
  });
  const { data: deviceData } = useRequest(getDevice, {
    defaultParams: [{ isAll: true }],
  });

  const { loading, run } = useRequest(createOtaJob, {
    manual: true,
    onSuccess: (resp) => {
      if (resp.success) {
        messageApi.success('升级任务已创建');
        form.resetFields();
        onSuccess();
        onClose();
      } else {
        messageApi.error(resp.errorMsg || '创建失败');
      }
    },
  });

  useEffect(() => {
    if (open && presetDeviceIds?.length) {
      form.setFieldsValue({ deviceIds: presetDeviceIds });
    }
  }, [open, presetDeviceIds, form]);

  const handleStart = async () => {
    try {
      const values = await form.validateFields();
      if (!values.deviceIds?.length) {
        messageApi.error('请选择至少一台设备');
        return;
      }
      run({
        firmwareId: values.firmwareId,
        deviceIds: values.deviceIds,
        retryMax: values.retryMax ?? 5,
        autoStart: true,
      });
    } catch (e) {
      /* 校验失败 */
    }
  };

  return (
    <Drawer title="创建升级任务" width={420} open={open} onClose={onClose} destroyOnClose>
      {contextHolder}
      <Alert type="info" showIcon style={{ marginBottom: 16 }} message="仅管理员可创建升级任务；存在活动升级任务的设备无法重复创建。" />
      <Form form={form} layout="vertical" initialValues={{ retryMax: 5 }}>
        <Form.Item name="firmwareId" label="选择固件" rules={[{ required: true, message: '请选择固件' }]}>
          <Select
            showSearch
            optionFilterProp="label"
            placeholder="选择固件"
            options={(firmwareData?.result?.list || []).map((f) => ({
              label: `${f.Name}（${f.Version}）`,
              value: f.Id,
            }))}
          />
        </Form.Item>
        <Form.Item name="deviceIds" label="选择设备" rules={[{ required: true, message: '请选择设备' }]}>
          <Select
            mode="multiple"
            showSearch
            optionFilterProp="label"
            placeholder="按名称/ID 搜索设备"
            options={(deviceData?.result?.list || []).map((d) => ({ label: `${d.name}（${d.id}）`, value: d.id }))}
          />
        </Form.Item>
        <Form.Item name="retryMax" label="单包重试次数">
          <InputNumber min={0} max={20} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item>
          <Space>
            <Button type="primary" loading={loading} onClick={handleStart}>
              开始升级
            </Button>
            <Button onClick={onClose}>取消</Button>
          </Space>
        </Form.Item>
      </Form>
    </Drawer>
  );
};

export default CreateJobDrawer;
